/** Error from the portal API, with the server's sentence and any field messages. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: { fields?: Record<string, string>; [k: string]: unknown },
  ) {
    super(message);
  }
  get fields(): Record<string, string> {
    return this.details?.fields ?? {};
  }
}

async function handle<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: any = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { error: text };
    }
  }
  if (!res.ok) {
    if (res.status === 401 && !location.pathname.startsWith('/login') && !location.pathname.startsWith('/invite')) {
      window.dispatchEvent(new CustomEvent('circdal:unauthorized'));
    }
    throw new ApiError(res.status, body?.error ?? `The request failed (${res.status}).`, body?.code, body?.details);
  }
  return body as T;
}

function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  return fetch(`/api${url}`, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then((r) => handle<T>(r));
}

export const api = {
  get: <T = any>(url: string) => request<T>('GET', url),
  post: <T = any>(url: string, body: unknown = {}) => request<T>('POST', url, body),
  patch: <T = any>(url: string, body: unknown = {}) => request<T>('PATCH', url, body),
  put: <T = any>(url: string, body: unknown = {}) => request<T>('PUT', url, body),
  del: <T = any>(url: string) => request<T>('DELETE', url),
  /** Sends multipart form data (files plus fields). */
  upload: <T = any>(url: string, form: FormData) =>
    fetch(`/api${url}`, { method: 'POST', credentials: 'same-origin', body: form }).then((r) => handle<T>(r)),
};

/** Builds a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
}

export function fileUrl(id: number, inline = false): string {
  // Preview builds point at recorded copies of the files.
  const demo = (window as unknown as { __circdalDemoFileUrl?: (id: number) => string }).__circdalDemoFileUrl;
  if (demo) return demo(id);
  return `/api/files/${id}/download${inline ? '?inline=1' : ''}`;
}

/**
 * The source for the in-page video player. The API answers with a redirect to a short-lived
 * bucket link that plays inline, so the browser's range requests go straight to storage.
 */
export function playUrl(id: number): string {
  const demo = (window as unknown as { __circdalDemoFileUrl?: (id: number) => string }).__circdalDemoFileUrl;
  if (demo) return demo(id);
  return `/api/files/${id}/play`;
}

/**
 * A gallery photo's web-size copy: the small thumbnail for grid tiles, or the display copy for
 * the lightbox. The camera original stays behind fileUrl() for "Download original".
 */
export function photoUrl(p: { file: { id: number }; thumbUrl: string; displayUrl: string }, size: 'thumb' | 'display'): string {
  // Preview builds have recorded copies of the originals only.
  const demo = (window as unknown as { __circdalDemoFileUrl?: (id: number) => string }).__circdalDemoFileUrl;
  if (demo) return demo(p.file.id);
  return size === 'thumb' ? p.thumbUrl : p.displayUrl;
}
